import { useState } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { KeyRound, CheckCircle2, UserCheck, Loader2, RotateCcw } from "lucide-react";

const CATEGORY_LABEL = { biomed: "أجهزة طبية", electrical: "أجهزة كهربائية", other: "أخرى" };
const fmtMonth = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString("ar-u-nu-latn", { month: "long", year: "numeric" }) : "—";

export default function PPMCheckin() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [pin, setPin] = useState("");
  const [visit, setVisit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const lookup = async () => {
    if (pin.trim().length < 4) { toast.error("أدخل رمز الحضور المكوّن من 6 أرقام"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ppm-checkin/lookup`, { pin: pin.trim() }, { headers });
      setVisit(res.data);
      setConfirmed(res.data.checked_in);
    } catch (e) { toast.error(e.response?.data?.detail || "رمز غير صحيح"); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/ppm-checkin/confirm`, { pin: pin.trim() }, { headers });
      setVisit(res.data);
      setConfirmed(true);
      toast.success("تم تسجيل حضور المقاول بنجاح — يمكنه الآن توثيق الزيارة من الرابط");
    } catch (e) { toast.error(e.response?.data?.detail || "فشل تسجيل الحضور"); }
    finally { setBusy(false); }
  };

  const reset = () => { setPin(""); setVisit(null); setConfirmed(false); };

  return (
    <div className="max-w-md mx-auto py-8" data-testid="ppm-checkin-page">
      <div className="text-center mb-6">
        <div className="w-14 h-14 rounded-2xl bg-[#655dc6] flex items-center justify-center mx-auto mb-3">
          <UserCheck className="w-7 h-7 text-white" />
        </div>
        <h1 className="text-xl font-bold text-slate-900">تسجيل حضور المقاول</h1>
        <p className="text-sm text-slate-500">أدخل رمز الحضور (PIN) الموجود في رسالة الواتساب لدى المقاول</p>
      </div>

      {!visit && (
        <div className="bg-white border rounded-2xl p-6 space-y-4 shadow-sm">
          <div className="relative">
            <KeyRound className="absolute start-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => e.key === "Enter" && lookup()}
              placeholder="######"
              className="ps-11 text-center text-2xl tracking-[0.5em] font-mono h-14"
              dir="ltr"
              inputMode="numeric"
              data-testid="checkin-pin-input"
            />
          </div>
          <Button onClick={lookup} disabled={busy} className="w-full bg-[#655dc6] hover:bg-[#5449a8] text-white h-11" data-testid="checkin-lookup-btn">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "بحث عن الزيارة"}
          </Button>
        </div>
      )}

      {visit && (
        <div className="bg-white border rounded-2xl p-6 space-y-4 shadow-sm" data-testid="checkin-visit-card">
          <div className="bg-[#655dc6]/5 border border-[#655dc6]/20 rounded-xl p-4 text-sm space-y-1.5">
            <p><span className="text-slate-500">العقد:</span> <span className="font-mono font-bold text-[#655dc6]" dir="ltr">{visit.contract_no}</span></p>
            <p><span className="text-slate-500">جهة الصيانة:</span> <span className="font-semibold">{visit.company_name}</span></p>
            {visit.contact_person && <p><span className="text-slate-500">المسؤول لديهم:</span> {visit.contact_person}</p>}
            <p><span className="text-slate-500">الفئة:</span> {CATEGORY_LABEL[visit.category]}</p>
            <p><span className="text-slate-500">الزيارة:</span> رقم {visit.visit_seq} — {fmtMonth(visit.scheduled_date)}</p>
          </div>

          {confirmed ? (
            <div className="text-center py-2" data-testid="checkin-success">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
              <p className="font-bold text-emerald-700">تم تسجيل الحضور</p>
              {visit.checked_in_at && <p className="text-xs text-slate-400 mt-1">بواسطة {visit.checked_in_by_name} — {new Date(visit.checked_in_at).toLocaleString("ar-u-nu-latn")}</p>}
              <p className="text-xs text-slate-500 mt-2">يمكن للمقاول الآن توثيق الزيارة والتوقيع من الرابط المرسل له</p>
            </div>
          ) : (
            <Button onClick={confirm} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="checkin-confirm-btn">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "تسجيل حضور المقاول الآن"}
            </Button>
          )}

          <Button variant="outline" onClick={reset} className="w-full gap-1.5" data-testid="checkin-reset-btn">
            <RotateCcw className="w-4 h-4" /> رمز آخر
          </Button>
        </div>
      )}
    </div>
  );
}
